import { useState, useEffect } from 'react';
import { useApi, type Track } from '../hooks/useApi';
import TrackCard from '../components/TrackCard';
import StartHereBlock from '../components/StartHereBlock';
import DailyInsight from '../components/DailyInsight';
import WhyThisMattersNow from '../components/WhyThisMattersNow';
import SocialProof from '../components/SocialProof';
import FAQ from '../components/FAQ';
import { Loader2, Sparkles, Zap, Brain, Microscope, Gift, Wrench } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

export default function Home() {
  const { fetchApi } = useApi();
  const [searchParams] = useSearchParams();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      // Store in sessionStorage for the registration page
      sessionStorage.setItem('referralCode', refCode.toUpperCase());

      // Validate and get referrer name
      fetch(`${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8787'}/api/referrals/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: refCode })
      })
        .then(res => res.json())
        .then(data => {
          if (data.valid && data.referrerName) {
            setReferrerName(data.referrerName);
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  useEffect(() => {
    loadTracks();
  }, []);

  async function loadTracks() {
    try {
      const data = await fetchApi<{ tracks: Track[] }>('/api/tracks');
      setTracks(data.tracks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracks');
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailSignup(e: React.FormEvent) {
    e.preventDefault();
    setSignupError(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8787'}/api/signups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'homepage' })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      setSubscribed(true);
      setEmail('');
    } catch (err) {
      setSignupError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const coreTracks = tracks.filter(t => t.level === 1);
  const labTracks = tracks.filter(t => t.level === 2);

  return (
    <div>
      {/* Referral Banner */}
      {referrerName && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-xl border border-purple-500/30">
          <div className="flex items-center justify-center gap-3">
            <Gift className="w-5 h-5 text-purple-400" />
            <p className="text-white">
              <span className="font-medium">{referrerName}</span> invited you!
              <Link to="/register" className="ml-2 text-purple-400 hover:text-purple-300 underline">
                Sign up now
              </Link> to claim your welcome bonus.
            </p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent-blue/10 rounded-full text-accent-blue text-sm mb-4 animate-fade-in-down">
          <Sparkles className="w-4 h-4" />
          A thinking-skills + AI lab for people who want to stay sharp
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 animate-fade-in-up opacity-0 stagger-1 tracking-tight">
          Northcoast AI Academy
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6 animate-fade-in-up opacity-0 stagger-2">
          Master critical thinking in the AI age, build powerful workflows, and apply AI to real-world domains.
        </p>

        {/* Hero CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap animate-fade-in-up opacity-0 stagger-3">
          <Link
            to="/shift"
            className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg"
          >
            <Zap className="w-4 h-4" />
            Take the 3-minute challenge
          </Link>
          <Link
            to="/bingo"
            className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg"
          >
            <Brain className="w-4 h-4" />
            Bias Bingo Quiz
          </Link>
          <Link
            to="/autopsy"
            className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-medium rounded-lg"
          >
            <Microscope className="w-4 h-4" />
            Prompt Autopsy
          </Link>
          <Link
            to="/fix-it"
            className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-accent-green text-slate-900 hover:opacity-90 font-medium rounded-lg"
          >
            <Wrench className="w-4 h-4" />
            Fix Your Computer with AI
          </Link>
          <a
            href="#tracks"
            className="btn-lift inline-flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg"
          >
            Browse tracks
          </a>
        </div>
        <p className="text-sm text-gray-400 mt-3 animate-fade-in opacity-0 stagger-4">
          We'll invite early access members to try new tracks, the Orientation Assessment, and AI-Trainer before anyone else.
        </p>
      </div>

      {/* Daily Insight */}
      <DailyInsight />

      {/* Start Here Block */}
      <StartHereBlock />

      {/* Why This Matters Now */}
      <WhyThisMattersNow />

      {/* Social Proof */}
      <SocialProof />

      {/* Core Tracks — the only part of the page that needs the API; a fetch
          failure must never blank the hero, free tools, or FAQ above/below. */}
      <section id="tracks" className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">Core Programs</h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" aria-label="Loading tracks" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-400">Couldn&rsquo;t load the track list. The free tools above still work!</p>
            <button
              onClick={() => { setError(null); setLoading(true); loadTracks(); }}
              className="mt-4 px-4 py-2 bg-accent-blue text-slate-900 rounded-lg hover:bg-blue-500 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {coreTracks.map(track => (
              <TrackCard key={track.id} track={track} />
            ))}
          </div>
        )}
      </section>

      {/* Lab Tracks */}
      {labTracks.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6">Applied Labs</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {labTracks.map(track => (
              <TrackCard key={track.id} track={track} />
            ))}
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <FAQ />

      {/* Email Signup Section */}
      <section id="signup" className="py-12 px-6 bg-gradient-to-r from-accent-blue/10 to-purple-500/10 rounded-2xl text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Stay in the loop</h2>
        <p className="text-gray-400 mb-6 max-w-lg mx-auto">
          We're rolling out new tracks, labs, and the Orientation Assessment over the next few months.
          Want early access? Drop your email—no spam, no AI sludge.
        </p>

        {subscribed ? (
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 text-green-400 rounded-lg">
            <Sparkles className="w-4 h-4" />
            Thanks! We'll be in touch.
          </div>
        ) : (
          <form onSubmit={handleEmailSignup} className="flex flex-col gap-3 max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-accent-blue disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-accent-blue hover:bg-blue-500 text-slate-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Signing up...' : 'Get early access'}
              </button>
            </div>
            {signupError && (
              <p className="text-red-400 text-sm">{signupError}</p>
            )}
          </form>
        )}
      </section>
    </div>
  );
}
